import React, { useState, useEffect } from "react";
import { FiUser, FiEdit2, FiCheck, FiMessageCircle, FiZap, FiShield, FiRefreshCw } from "react-icons/fi";
import Modal from "@/components/modal";
import toast from "react-hot-toast";
import api from "@/services/api";

// ============================================
// ImproveWithAIModal (inline)
// ============================================
function ImproveWithAIModal({ isOpen, onClose, currentBio, onApply }) {
  const [selectedTone, setSelectedTone] = useState("professional");
  const [isGenerating, setIsGenerating] = useState(false);
  const [improvedBio, setImprovedBio] = useState(null);

  const tones = [
    {
      id: "professional",
      label: "Professional",
      description: "Formal & polished",
      icon: FiShield,
    },
    {
      id: "friendly",
      label: "Friendly",
      description: "Warm & approachable",
      icon: FiMessageCircle,
    },
    {
      id: "confident",
      label: "Confident",
      description: "Bold & impactful",
      icon: FiZap,
    },
  ];

  const improvements = [
    "Fix grammar and awkward phrasing",
    "Highlight your key strengths",
    "Optimize for recruiter scanning",
    "Add relevant keywords",
  ];

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const { ok, data } = await api.post("/user/improve-bio", {
        bio: currentBio,
        tone: selectedTone,
      });
      if (!ok) throw new Error("Failed to generate");
      setImprovedBio(data.improved_bio);
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate improved bio");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setSelectedTone("professional");
    setImprovedBio(null);
    onClose();
  };

  const handleApply = () => {
    onApply(improvedBio);
    setImprovedBio(null);
  };

  const handleTryAgain = () => {
    setImprovedBio(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="w-full max-w-2xl">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg text-white">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{improvedBio ? "Your improved bio" : "Improve with AI"}</h2>
            <p className="text-gray-500 text-sm">{improvedBio ? "Review and apply the changes" : "Make your bio stand out to recruiters"}</p>
          </div>
        </div>

        {improvedBio ? (
          /* Result View */
          <div className="space-y-6">
            {/* Before/After comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Before</label>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 h-full">
                  <p className="text-gray-600 leading-relaxed text-sm">{currentBio}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-emerald-600 mb-2 flex items-center gap-1">
                  <FiZap className="w-3 h-3" /> After
                </label>
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 h-full">
                  <p className="text-gray-800 leading-relaxed text-sm font-medium">{improvedBio}</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleTryAgain}
                className="flex-1 btn btn-secondary cursor-pointer flex items-center justify-center gap-2"
              >
                <FiRefreshCw className="w-4 h-4" />
                Try again
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="flex-1 btn btn-primary cursor-pointer flex items-center justify-center gap-2"
              >
                <FiCheck className="w-4 h-4" />
                Apply this bio
              </button>
            </div>
          </div>
        ) : (
          /* Generation View */
          <>
            <div className="space-y-6">
              {/* Current Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your current bio</label>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <p className="text-gray-700 leading-relaxed italic">
                    {currentBio || "No bio provided yet."}
                  </p>
                </div>
                <p className="text-sm text-gray-500 mt-2">{currentBio?.length || 0} characters</p>
              </div>

              {/* Tone Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Choose a tone</label>
                <div className="grid grid-cols-3 gap-3">
                  {tones.map((tone) => {
                    const Icon = tone.icon;
                    const isSelected = selectedTone === tone.id;
                    return (
                      <button
                        key={tone.id}
                        type="button"
                        onClick={() => setSelectedTone(tone.id)}
                        className={`p-4 rounded-xl border-2 transition-all text-center cursor-pointer ${
                          isSelected 
                            ? "border-indigo-500 bg-indigo-50" 
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                          isSelected ? "bg-indigo-100 text-indigo-600" : "bg-gray-100 text-gray-500"
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <p className={`font-semibold text-sm ${isSelected ? "text-indigo-600" : "text-gray-900"}`}>
                          {tone.label}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">{tone.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* What AI will improve */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="font-semibold text-gray-900 mb-3">What the AI will improve:</p>
                <ul className="space-y-2">
                  {improvements.map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                      <FiCheck className="w-4 h-4 text-blue-600 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 pt-6 mt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 btn btn-secondary cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || !currentBio}
                className="flex-1 btn btn-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" />
                    </svg>
                    Generate improved bio
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ============================================
// AboutSection Component
// ============================================
export default function AboutSection({ values, setValues, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aboutText, setAboutText] = useState(values.about || "");

  useEffect(() => {
    setAboutText(values.about || "");
  }, [values.about]);

  const handleSave = async () => {
    await onSave({ about: aboutText });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setAboutText(values.about || "");
    setIsEditing(false);
  };

  const handleApplyImprovedBio = (improvedBio) => {
    setAboutText(improvedBio);
    setShowAIModal(false);
    setIsEditing(true);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
            <FiUser className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">About</h2>
        </div>
        <button 
          onClick={() => setIsEditing(!isEditing)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 cursor-pointer"
        >
          <FiEdit2 className="w-4 h-4" />
          Edit
        </button>
      </div>
      
      {isEditing ? (
        <div className="space-y-4">
          <textarea
            value={aboutText}
            onChange={(e) => setAboutText(e.target.value)}
            className="w-full min-h-[150px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y"
            placeholder="Tell us about yourself..."
          />
          <div className="flex justify-end gap-3">
            <button 
              onClick={handleCancel}
              className="btn btn-secondary cursor-pointer"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className="btn btn-primary cursor-pointer"
            >
              Save Changes
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
            {values.about || "No description provided yet. Add a bio to tell recruiters about yourself."}
          </p>
          <button 
            onClick={() => setShowAIModal(true)}
            className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" />
            </svg>
            Improve my bio
          </button>
        </div>
      )}

      <ImproveWithAIModal 
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        currentBio={values.about}
        onApply={handleApplyImprovedBio}
      />
    </div>
  );
}
